import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "sonner";
import * as ApiService from "@/services/ApiService";
import taskRequest from "@/api/task-request";
import type { CaseHistoryOat } from "@/types/api";
import { limitDistinctOats, outcomeAssessmentLimitFor } from "@/types/task-actions";
import {
  useProfileStore,
  getFacilityKey,
  getFacilityKeyForPortal,
  getPatientKey,
} from "./profile";
import { useTaskStore } from "./task";

/** Map GetCaseHistoryOats (PatientPortal) response item to ProblemOat (prompt, questions with description → question, answer, answers). */
export function mapCaseHistoryOatToProblemOat(cho: CaseHistoryOat): ProblemOat {
  return {
    oatID: cho.oatID,
    prompt: cho.prompt ?? "",
    questions: (cho.questions ?? []).map((q, index) => ({
      ordinal: q.oatQuestionID ?? index,
      question: q.description ?? "",
      answer: q.answer ?? "",
      answers: Array.isArray(q.answers) ? q.answers : [],
    })),
  };
}

export type SelectedBodyPart = {
  partName: string;
  side: string;
  label: string;
};

export interface ComplaintValues {
  code: string;
  value: string;
}

export interface ComplaintHeader {
  id?: string | number;
  complaintType: string;
  region: string;
  description?: string;
  complete?: boolean;
}

/** Portal API GetCaseHistoryProblems response item (swagger CaseHistoryProblem) */
interface CaseHistoryProblem {
  id: number;
  ordinal?: number;
  complaintType?: string | null;
  region?: string | null;
  name?: string | null;
  oatID?: number;
  oatDescription?: string | null;
  complete?: boolean;
}

export interface PrimaryComplaintDemographics {
  firstName: string;
  mi: string;
  lastName: string;
  nickname: string;
  dob: string;
  gender: string;
  maritalStatID: number;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phoneType: string;
  phone: string;
  email: string;
  contactMethod: string;
  emerContact: string;
  emerRelation: string;
  emerPhone: string;
}

export interface HumanBodyState {
  selectedPart?: SelectedBodyPart | null;
  selectedInternalPart?: string | null;
}

export enum VisitReason {
  PAIN = "pain",
  NUMBNESS = "numbness",
  WEAKNESS = "weakness",
  HEADACHES = "headaches",
  DIZZINESS = "dizziness",
  WELLNESS = "wellness",
}

export enum ComplaintHistoryStep {
  ComplaintHistory1 = "ComplaintHistory1",
  ComplaintHistory2 = "ComplaintHistory2",
  ComplaintHistory3 = "ComplaintHistory3",
}

export enum QualityOptions {
  sharp = "sharp",
  dull = "dull",
  aching = "aching",
  throbbing = "throbbing",
  burning = "burning",
  stabbing = "stabbing",
  shooting = "shooting",
  tingling = "tingling",
  Numbness = "numbness",
  Other = "other",
}

export interface OatQuestion {
  ordinal: number;
  question: string;
  answer: string;
  answers: string[];
}

export interface ProblemOat {
  oatID: number;
  prompt: string;
  questions: OatQuestion[];
}

const visitReasonLabels: Record<VisitReason, string> = {
  [VisitReason.PAIN]: "Pain",
  [VisitReason.NUMBNESS]: "Numbness",
  [VisitReason.WEAKNESS]: "Weakness",
  [VisitReason.HEADACHES]: "Headaches",
  [VisitReason.DIZZINESS]: "Dizziness",
  [VisitReason.WELLNESS]: "Wellness",
};

function getPatientId(): string {
  const p = useProfileStore.getState().currentPatient;
  return p?.patientID?.toString() ?? p?.id?.toString() ?? "";
}

/** Portal routes expect facility GUID when available (see getFacilityKeyForPortal). */
function portalFacilityKey(): string {
  return getFacilityKeyForPortal() || getFacilityKey();
}

/**
 * `/Portal/.../{patientKey}` must use the opaque portal key when the API issued one.
 * Call sites often pass numeric `patientID`; if it matches the logged-in patient,
 * prefer link key → `currentPatient.patientKey` → numeric id — otherwise 403 Forbidden.
 */
function resolvePortalPatientKey(explicit?: string | number | null): string {
  const state = useProfileStore.getState();
  const p = state.currentPatient;
  const linkKey =
    state.linkInfo?.patientKey && String(state.linkInfo.patientKey).trim()
      ? String(state.linkInfo.patientKey).trim()
      : "";
  const opaque =
    p?.patientKey && String(p.patientKey).trim() ? String(p.patientKey).trim() : "";
  const pid = p?.patientID != null ? String(p.patientID) : "";

  if (explicit == null || String(explicit).trim() === "") {
    return linkKey || opaque || pid;
  }
  const want = String(explicit).trim();
  if (pid && want === pid) {
    if (linkKey) return linkKey;
    if (opaque) return opaque;
    return pid;
  }
  return want;
}

/**
 * Resolve case history for this patient via the API. Do not use a stale
 * persisted `caseHistory.id` — it may belong to a previously selected patient.
 */
async function ensureCaseHistoryIdForPatient(
  facilityKey: string,
  patientKey: string
): Promise<number> {
  if (!facilityKey || !patientKey) return 0;
  const findRes = await taskRequest.request.findIncompleteCaseHistory(
    facilityKey,
    patientKey
  );
  const foundId =
    typeof findRes?.data === "number"
      ? findRes.data
      : (findRes?.data as { id?: number } | undefined)?.id;
  if (foundId != null && foundId > 0) {
    useTaskStore.getState().setCurrentCaseHistoryId(foundId);
    return foundId;
  }
  const insertRes = await taskRequest.request.insertCaseHistory(
    facilityKey,
    patientKey,
    { aa: "false" }
  );
  const newId =
    typeof insertRes?.data === "number"
      ? insertRes.data
      : (insertRes?.data as { id?: number } | undefined)?.id;
  if (newId != null && newId > 0) {
    useTaskStore.getState().setCurrentCaseHistoryId(newId);
    return newId;
  }
  return 0;
}

interface ComplaintHistoryState {
  step: ComplaintHistoryStep;
  answers: {
    onset: string;
    specificDate: string;
    start: string;
    startDetails: string;
    severityRating: number;
    frequency: string;
    qualities: QualityOptions[];
  };
}

interface PrimaryComplaintConfig {
  gender: "M" | "F" | null;
}

interface PrimaryComplaintStoreState {
  loading: boolean;
  error: string | null;
  bodyMap: string[] | null;
  humanBody: HumanBodyState | null;
  visitReason: VisitReason | null;
  config: PrimaryComplaintConfig;
  complaintHistory: ComplaintHistoryState;
  generalRegion: string | null;
  demographics: PrimaryComplaintDemographics | null;
  complaintHeaders: ComplaintHeader[] | null;
  currentComplaintHeader: ComplaintHeader | null;
  currentComplaintValues: ComplaintValues[] | null;
  problemOat: ProblemOat | null;
  /** Number of complaint/outcome saves currently in flight (drives the leave-page guard). */
  pendingSaves: number;
  setHumanBody: (newHumanBody: Partial<HumanBodyState>) => void;
  clearHumanBody: () => void;
  clearBodyMap: () => void;
  setVisitReason: (reason: VisitReason | null) => void;
  resetVisitReason: () => void;
  setProblemOat: (oat: ProblemOat) => void;
  setComplaintHistoryStep: (step: ComplaintHistoryStep) => void;
  startNewComplaint: () => void;
  fetchBodyMap: (partName: string, side: string) => Promise<void>;
  fetchComplaints: (
    patientID?: string | number
  ) => Promise<ComplaintHeader[] | null>;
  createComplaint: (
    id?: string | number,
    description?: string
  ) => Promise<unknown>;
  updateComplaint: (
    problemId: string | number,
    patientKey?: string
  ) => Promise<void>;
  fetchComplaintValues: (
    patientID?: string | number,
    complaintId?: string
  ) => Promise<unknown>;
  fetchComplaintValue: (complaintId: string, code: string) => Promise<unknown>;
  updateComplaintValue: (code: string, value: unknown) => Promise<unknown>;
  fetchProblemOat: (intakeProblemId: string | number) => Promise<unknown>;
  fetchCaseHistoryOats: (
    facilityId: string | number,
    caseHistoryId: string | number,
    caseHistoryProblemID?: string | number
  ) => Promise<unknown>;
  updateProblemOat: (
    intakeProblemId: string | number,
    oat: ProblemOat,
    index: number
  ) => Promise<unknown>;
  fetchRadiatingList: (region: string) => Promise<unknown>;
  completeComplaint: (
    intakeProblemId: string | number,
    complete?: boolean
  ) => Promise<unknown>;
  deleteComplaint: (
    problemId: string | number,
    patientKey?: string
  ) => Promise<void>;
  clearData: () => void;
}

/**
 * Surface a save failure to the patient so nothing is ever lost silently. A stable toast id means
 * repeated failures on a flaky connection replace the message rather than stacking up.
 */
function notifySaveFailure() {
  toast.error(
    "We couldn't save your last answer — check your connection and try again. Your entries are still here.",
    { id: "complaint-save-error" }
  );
}

const defaultComplaintHistory: ComplaintHistoryState = {
  step: ComplaintHistoryStep.ComplaintHistory1,
  answers: {
    onset: "",
    specificDate: "",
    start: "",
    startDetails: "",
    severityRating: 5,
    frequency: "",
    qualities: [],
  },
};

/** In-flight save per complaint field ("problemId:code"); the next save of that field waits for it. */
const saveChains = new Map<string, Promise<unknown>>();

export const usePrimaryComplaintStore = create<PrimaryComplaintStoreState>()(
  persist(
    (set, get) => ({
      loading: false,
      error: null,
      bodyMap: null,
      humanBody: null,
      visitReason: null,
      config: { gender: null },
      complaintHistory: defaultComplaintHistory,
      generalRegion: null,
      demographics: null,
      complaintHeaders: null,
      currentComplaintHeader: null,
      currentComplaintValues: null,
      problemOat: null,
      pendingSaves: 0,

      setHumanBody: (newHumanBody) =>
        set((s) => ({
          humanBody: s.humanBody
            ? { ...s.humanBody, ...newHumanBody }
            : { selectedPart: undefined, selectedInternalPart: undefined, ...newHumanBody },
        })),

      clearHumanBody: () => set({ humanBody: null }),
      clearBodyMap: () => set({ bodyMap: null }),

      setVisitReason: (reason) => set({ visitReason: reason }),
      resetVisitReason: () => set({ visitReason: null }),

      setProblemOat: (oat) => set({ problemOat: oat }),

      setComplaintHistoryStep: (step) =>
        set((s) => ({
          complaintHistory: { ...s.complaintHistory, step },
        })),

      startNewComplaint: () =>
        set({
          visitReason: null,
          humanBody: null,
          complaintHistory: defaultComplaintHistory,
          complaintHeaders: null,
          currentComplaintHeader: null,
          currentComplaintValues: null,
          problemOat: null,
        }),

      fetchBodyMap: async (partName, side) => {
        set({ loading: true, error: null, bodyMap: null });
        const locale = "en";
        try {
          const response = await ApiService.get(
            `/Portal/GetRegions/${portalFacilityKey()}/${side}/${partName}/${locale}`
          );
          set({ bodyMap: response.data as string[] });
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
        } finally {
          set({ loading: false });
        }
      },

      fetchComplaints: async (patientID = getPatientKey() || getPatientId()) => {
        set({ loading: true, error: null, complaintHeaders: null });
        const facilityKey = portalFacilityKey();
        const patientKey = resolvePortalPatientKey(patientID);
        try {
          const caseHistoryId = await ensureCaseHistoryIdForPatient(
            facilityKey,
            patientKey
          );
          if (caseHistoryId === 0) {
            set({ complaintHeaders: [] });
            return [];
          }
          const response = await taskRequest.request.getCaseHistoryProblems(
            facilityKey,
            caseHistoryId
          );
          const problems = (response.data as CaseHistoryProblem[]) ?? [];
          const headers: ComplaintHeader[] = problems.map((p) => ({
            id: p.id,
            complaintType: p.complaintType ?? "",
            region: p.region ?? "",
            description: p.name ?? undefined,
            complete: p.complete,
          }));
          set({ complaintHeaders: headers });
          return headers;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      createComplaint: async (
        id = getPatientKey() || getPatientId(),
        description = ""
      ) => {
        set({ loading: true, error: null });
        const state = get();
        const facilityKey = portalFacilityKey();
        const patientKey = resolvePortalPatientKey(id);
        try {
          const caseHistoryId = await ensureCaseHistoryIdForPatient(
            facilityKey,
            patientKey
          );
          if (caseHistoryId === 0) {
            throw new Error("Could not find or create case history");
          }
          const problemsRes = await taskRequest.request.getCaseHistoryProblems(
            facilityKey,
            caseHistoryId
          );
          const problems = (problemsRes.data as CaseHistoryProblem[]) ?? [];
          const ordinal = problems.length + 1;
          const complaintType = state.visitReason ?? "";
          const regionFromBody = state.humanBody?.selectedPart?.label ?? "";
          const nameFromList = state.humanBody?.selectedInternalPart ?? "";
          const region = regionFromBody || nameFromList;
          const complaintTypeLabel = complaintType
            ? complaintType.charAt(0).toUpperCase() + complaintType.slice(1)
            : "";
          const name =
            complaintTypeLabel && nameFromList
              ? `${complaintTypeLabel} in the ${nameFromList}`
              : nameFromList || description;
          const response = await taskRequest.request.insertCaseHistoryProblem(
            facilityKey,
            caseHistoryId,
            ordinal,
            {
              complaintType,
              region,
              name: name || "",
            }
          );
          const raw = response.data as { id?: number } | number | undefined;
          const problemId =
            typeof raw === "number" ? raw : raw?.id;
          const header: ComplaintHeader = {
            id: problemId,
            complaintType,
            region,
            description: name || undefined,
          };
          set({ currentComplaintHeader: header });
          return problemId;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateComplaint: async (problemId, explicitPatientKey = getPatientKey() || getPatientId()) => {
        const id = Number(problemId);
        if (Number.isNaN(id)) return;
        const state = get();
        const facilityKey = portalFacilityKey();
        const patientKey = resolvePortalPatientKey(explicitPatientKey);
        const complaintType = state.visitReason ?? "";
        const regionFromBody = state.humanBody?.selectedPart?.label ?? "";
        const nameFromList = state.humanBody?.selectedInternalPart ?? "";
        const region = regionFromBody || nameFromList;
        const complaintTypeLabel = complaintType
          ? complaintType.charAt(0).toUpperCase() + complaintType.slice(1)
          : "";
        const name =
          complaintTypeLabel && nameFromList
            ? `${complaintTypeLabel} in the ${nameFromList}`
            : nameFromList || "";
        set({ loading: true, error: null });
        try {
          await taskRequest.request.updateCaseHistoryProblem(facilityKey, id, {
            complaintType,
            region,
            name: name || "",
          });
          const patient = String(patientKey ?? "");
          await get().fetchComplaints(patient);
          const headers = get().complaintHeaders ?? [];
          const updated = headers.find((h) => Number(h.id) === id);
          if (updated) {
            set({
              currentComplaintHeader: {
                ...updated,
                complaintType,
                region,
                description: name || undefined,
              },
            });
          }
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      fetchComplaintValues: async (
        // Kept for call-site compatibility; the id actually comes from currentComplaintHeader below.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _patientID = getPatientId(),
        complaintId = get().currentComplaintHeader?.id
      ) => {
        if (complaintId == null) return;
        const problemId = Number(complaintId);
        if (Number.isNaN(problemId)) return;
        set({ loading: true, error: null });
        try {
          const response = await taskRequest.request.getCaseHistoryProblemValues(
            portalFacilityKey(),
            problemId
          );
          const values = (response.data as ComplaintValues[]) ?? null;
          set({ currentComplaintValues: values });
          return response.data;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          console.error("API Error:", err);
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      fetchComplaintValue: async (complaintId, code) => {
        const problemId = Number(complaintId);
        if (Number.isNaN(problemId)) return;
        set({ loading: true, error: null });
        try {
          const response = await taskRequest.request.getCaseHistoryProblemValues(
            portalFacilityKey(),
            problemId
          );
          const list = (response.data as ComplaintValues[]) ?? [];
          const item = list.find((v) => v.code === code);
          return item?.value;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateComplaintValue: async (code, value) => {
        const complaintId = get().currentComplaintHeader?.id;
        if (complaintId == null) return;
        const problemId = Number(complaintId);
        if (Number.isNaN(problemId)) return;
        set((s) => ({ loading: true, error: null, pendingSaves: s.pendingSaves + 1 }));
        // Saves of the same field run one after another, so the patient's last tap is the last write.
        // Unordered requests could land out of order and leave an answer the patient took back.
        const key = `${problemId}:${code}`;
        const previous = saveChains.get(key) ?? Promise.resolve();
        const run = previous
          .catch(() => {})
          .then(() =>
            taskRequest.request.updateCaseHistoryProblemValue(portalFacilityKey(), problemId, {
              code,
              value: value != null ? String(value) : undefined,
            })
          );
        saveChains.set(key, run);
        try {
          const response = await run;
          return response.data;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          notifySaveFailure();
          throw err;
        } finally {
          if (saveChains.get(key) === run) saveChains.delete(key);
          set((s) => ({ loading: false, pendingSaves: Math.max(0, s.pendingSaves - 1) }));
        }
      },

      fetchProblemOat: async (intakeProblemId) => {
        const problemId = Number(intakeProblemId);
        if (Number.isNaN(problemId)) return null;
        set({ loading: true, error: null, problemOat: null });
        try {
          const response = await taskRequest.request.getCaseHistoryProblemOat(
            portalFacilityKey(),
            problemId
          );
          const raw = response.data as CaseHistoryOat | null | undefined;
          // Without the case history's full list the distinct-assessment order is unknown here,
          // so only "no outcome assessments" (limit 0) is enforced on this path.
          const oatLimit = outcomeAssessmentLimitFor(useTaskStore.getState().portalConfigMeta);
          const data =
            raw && typeof raw === "object" && raw.oatID != null && oatLimit !== 0
              ? mapCaseHistoryOatToProblemOat(raw)
              : null;
          set({ problemOat: data });
          return data;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      fetchCaseHistoryOats: async (
        facilityId,
        caseHistoryId,
        caseHistoryProblemID
      ) => {
        set({ loading: true, error: null, problemOat: null });
        try {
          const response = await taskRequest.request.getCaseHistoryOats(
            String(facilityId),
            Number(caseHistoryId)
          );
          const raw = response.data;
          // Unwrap: API may return array or { oats: [] } / { data: [] }
          const fullList: CaseHistoryOat[] = Array.isArray(raw)
            ? raw
            : raw && typeof raw === "object"
              ? (raw as { oats?: CaseHistoryOat[] }).oats ?? (raw as { data?: CaseHistoryOat[] }).data ?? (raw ? [raw as CaseHistoryOat] : [])
              : [];
          // The config caps how many distinct assessments the patient is asked across all their
          // complaints: keep the first N in the API's order; later complaints sharing one still get it.
          const list = limitDistinctOats(
            fullList,
            outcomeAssessmentLimitFor(useTaskStore.getState().portalConfigMeta)
          );
          const problemId =
            caseHistoryProblemID != null
              ? Number(caseHistoryProblemID)
              : undefined;
          // Only show the OAT for this complaint; never show all or another complaint's questions
          const cho =
            problemId != null
              ? list.find((o) => o.caseHistoryProblemID === problemId)
              : list[0];
          const data = cho ? mapCaseHistoryOatToProblemOat(cho) : null;
          set({ problemOat: data });
          return data;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateProblemOat: async (intakeProblemId, oat, index) => {
        const problemId = Number(intakeProblemId);
        if (Number.isNaN(problemId)) return;
        set((s) => ({ loading: true, error: null, pendingSaves: s.pendingSaves + 1 }));
        try {
          await taskRequest.request.updateCaseHistoryProblemOatAnswers(
            portalFacilityKey(),
            problemId,
            oat.oatID,
            [
              {
                oatID: oat.oatID,
                question: oat.questions[index]?.question,
                answer: oat.questions[index]?.answer,
              },
            ]
          );
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          notifySaveFailure();
          throw err;
        } finally {
          set((s) => ({ loading: false, pendingSaves: Math.max(0, s.pendingSaves - 1) }));
        }
      },

      fetchRadiatingList: async (region) => {
        set({ loading: true, error: null });
        const normalize = (data: unknown): string[] => {
          if (typeof data === "string") {
            try {
              return JSON.parse(data) as string[];
            } catch {
              return [];
            }
          }
          return Array.isArray(data) ? data : [];
        };
        try {
          const response = await ApiService.get(
            `/Portal/RadiatingList/${encodeURIComponent(region)}`
          );
          return normalize(response.data);
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      completeComplaint: async (intakeProblemId, complete = true) => {
        const problemId = Number(intakeProblemId);
        if (Number.isNaN(problemId)) return;
        set({ loading: true, error: null });
        try {
          const response = await taskRequest.request.updateCaseHistoryProblemComplete(
            portalFacilityKey(),
            problemId,
            complete
          );
          return response.data;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          toast.error(
            "We couldn't finish saving this complaint — check your connection and try again.",
            { id: "complaint-complete-error" }
          );
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      deleteComplaint: async (problemId, patientKey = getPatientKey() || getPatientId()) => {
        const id = Number(problemId);
        if (Number.isNaN(id)) return;
        const facilityKey = portalFacilityKey();
        const patient = String(patientKey ?? "");
        const caseHistoryId = await ensureCaseHistoryIdForPatient(
          facilityKey,
          patient
        );
        if (caseHistoryId === 0) {
          throw new Error("Could not find case history");
        }
        set({ loading: true, error: null });
        try {
          await taskRequest.request.deleteCaseHistoryProblem(
            facilityKey,
            caseHistoryId,
            id
          );
          await get().fetchComplaints(patient);
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "An error occurred",
          });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      clearData: () => set({ bodyMap: null, error: null }),
    }),
    {
      name: "primary-complaint",
      /** Do not persist complaint lists — they are patient-specific and come from the API. */
      partialize: (state) => ({
        ...state,
        complaintHeaders: null,
        currentComplaintHeader: null,
        currentComplaintValues: null,
        problemOat: null,
        loading: false,
        error: null,
        // A save in flight when the tab closed never decrements; persisting it would trigger the
        // "leave site?" prompt forever. The body-map list belongs to one complaint's add form.
        pendingSaves: 0,
        bodyMap: null,
      }),
    }
  )
);

export { visitReasonLabels };
